import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, Icon, Badge, Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";
import { FaShieldAlt, FaFileInvoiceDollar, FaChartLine, FaHome, FaTools, FaPercentage } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const TEAL = "#0D7F6E";
const SAND = "#C2A878";
const OFF_WHITE = "#F0EBE0";

const PropertyServicesTaxProtection: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/services/tax-protection"
        fallbackTitle="Tax Minimisation Strategies – One Property Australia"
        fallbackDescription="Minimize tax on property investments. Legal strategies for high-income earners to reduce tax while building long-term wealth."
      />
      <NavbarWithCallToAction />

      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaShieldAlt} boxSize={16} />
            <Heading size="3xl" fontWeight="black">Tax Minimisation Strategies</Heading>
            <Text fontSize="xl" maxW="3xl">Earning $200k+ means paying significant tax. Property investment offers powerful, legal strategies to reduce your tax bill while building long-term wealth.</Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <Tabs variant="soft-rounded" colorScheme="teal" size="lg">
          <TabList justifyContent="center" mb={12} flexWrap="wrap">
            <Tab>Deductions</Tab>
            <Tab>Depreciation</Tab>
            <Tab>Structures</Tab>
            <Tab>CGT</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {["Loan Interest", "Property Management", "Council Rates", "Insurance", "Repairs & Maintenance", "Utilities"].map((item) => (
                  <Box key={item} bg={OFF_WHITE} p={6} borderRadius="xl" border="1px solid" borderColor={SAND}>
                    <Icon as={FaFileInvoiceDollar} color={TEAL} boxSize={8} mb={3} />
                    <Heading size="sm" mb={2}>{item}</Heading>
                    <Text fontSize="sm" color="gray.600">100% tax deductible</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </TabPanel>

            <TabPanel>
              <VStack spacing={6}>
                <Text fontSize="lg" color="gray.600">Depreciation schedules unlock hidden deductions</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                  <Box bg={OFF_WHITE} p={6} borderRadius="xl" border="1px solid" borderColor={SAND}>
                    <Heading size="md" mb={3} color={TEAL}>Building Write-Off</Heading>
                    <Text color="gray.600">2.5% per year for 40 years</Text>
                  </Box>
                  <Box bg="gray.50" p={6} borderRadius="xl">
                    <Heading size="md" mb={3} color={NAVY}>Plant & Equipment</Heading>
                    <Text color="gray.600">Various rates for fixtures & fittings</Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </TabPanel>

            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Box bg={OFF_WHITE} p={6} borderRadius="xl" textAlign="center" border="1px solid" borderColor={SAND}>
                  <Heading size="md" mb={3} color={TEAL}>Trust Structure</Heading>
                  <Text fontSize="sm" color="gray.600">Asset protection & flexibility</Text>
                </Box>
                <Box bg="gray.50" p={6} borderRadius="xl" textAlign="center">
                  <Heading size="md" mb={3} color={NAVY}>Company</Heading>
                  <Text fontSize="sm" color="gray.600">Fixed tax rate benefits</Text>
                </Box>
                <Box bg="gray.50" p={6} borderRadius="xl" textAlign="center">
                  <Heading size="md" mb={3} color={NAVY}>Individual</Heading>
                  <Text fontSize="sm" color="gray.600">Simple & straightforward</Text>
                </Box>
              </SimpleGrid>
            </TabPanel>

            <TabPanel>
              <VStack spacing={6}>
                <Heading size="md">Capital Gains Tax Strategies</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                  <Box bg={OFF_WHITE} p={6} borderRadius="xl" border="1px solid" borderColor={SAND}>
                    <Icon as={FaPercentage} boxSize={8} color={TEAL} mb={3} />
                    <Heading size="sm" mb={2}>50% Discount</Heading>
                    <Text fontSize="sm" color="gray.600">Hold for 12+ months</Text>
                  </Box>
                  <Box bg="gray.50" p={6} borderRadius="xl">
                    <Icon as={FaHome} boxSize={8} color={NAVY} mb={3} />
                    <Heading size="sm" mb={2}>Main Residence Exemption</Heading>
                    <Text fontSize="sm" color="gray.600">Strategic use of PPOR status</Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      <Box bg={TEAL} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaShieldAlt} boxSize={16} />
            <Heading size="xl">Stop Paying More Tax Than You Need To</Heading>
            <Text fontSize="lg" maxW="2xl">Most high-income earners don't realise how much tax they could legally save through strategic property investment. Book a consultation to discover your options.</Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyServicesTaxProtection;
